import { getUserOccupation } from '~/modules/user';
import type {
    Conversation,
    DirectConversation,
    EnquiryConversation,
    Participant,
    ProfessionalIdentity,
} from '../types';

/**
 * The maximum number of participants that can be added to a conversation. Must
 * match the value in `ConversationService::MAX_PARTICIPANTS`.
 * @see {@link app/src/Component/Conversation/Core/Service/ConversationService.php}
 */
export const MAX_PARTICIPANTS = 200;

/**
 * Get the display name of a participant in a conversation.
 * @param participant The participant to get the name for.
 * @returns The display name of the participant.
 */
export function getParticipantName(
    participant:
        | Participant
        | Pick<
              Extract<Participant['Identity'], { __typename: 'Professional' }>,
              '__typename' | 'Title'
          >
        | Pick<
              Extract<Participant['Identity'], { __typename: 'Member' }>,
              '__typename' | 'Name'
          >,
): string {
    const identity =
        'Identity' in participant ? participant.Identity : participant;
    return identity.__typename === 'Professional'
        ? identity.Title
        : identity.Name;
}

/**
 * Check if the given professional is the recipient professional of the enquiry
 * conversation. This is the professional that the enquiry was sent to, or a
 * branch of the professional that the enquiry was sent to.
 * @param conversation The conversation to check.
 * @param professionalID The ID of the professional to check.
 * @returns The given professional is the recipient professional of the enquiry conversation.
 */
function isEnquiryConversationProfessional(
    conversation: EnquiryConversation,
    professionalID: number,
) {
    return conversation.Participants.some(
        ({ Identity }) =>
            Identity.__typename === 'Professional' &&
            Identity.ID === professionalID,
    );
}

/**
 * Check if the given user is a collaborator in an enquiry conversation.
 * @param conversation The conversation to check.
 * @param userID The ID of the current user.
 * @returns The given user is a collaborator in the enquiry conversation.
 */
function isEnquiryCollaborator(
    conversation: EnquiryConversation,
    userID: number,
): boolean {
    return conversation.Participants.some(
        ({ Identity, IsCollaborator }) =>
            Identity.__typename === 'Member' &&
            Identity.ID === userID &&
            IsCollaborator,
    );
}

/**
 * Check if the logged-in user is on the professional side of an enquiry
 * conversation (company inbox or personal inbox as a collaborator).
 * @param conversation The conversation to check.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The given user is on the professional side of an enquiry conversation.
 */
export function isEnquiryProfessionalSide(
    conversation: Conversation | null | undefined,
    userID: number,
    professionalID?: number | null,
): boolean {
    if (conversation?.__typename !== 'EnquiryConversation') {
        return false;
    }
    if (professionalID && professionalID > 0) {
        return isEnquiryConversationProfessional(conversation, professionalID);
    }
    return isEnquiryCollaborator(conversation, userID);
}

/**
 * Get the enquired professional participant of an enquiry conversation. This is
 * the professional that the enquiry was sent to. For conversations with multiple
 * professional participants, this will return the branch professional.
 */
export function getEnquiryConversationProfessional(
    conversation: EnquiryConversation,
): ProfessionalIdentity {
    const professionalParticipants = conversation.Participants.filter(
        ({ Identity }) => Identity.__typename === 'Professional',
    );
    let participant = professionalParticipants[0];
    if (professionalParticipants.length > 1) {
        participant =
            professionalParticipants.find(
                ({ IsCollaborator }) => IsCollaborator,
            ) ?? participant;
    }
    return participant?.Identity.__typename === 'Professional'
        ? participant.Identity
        : conversation.InitialEnquiry.Professional;
}

/**
 * Get all the participants of a chat conversation, excluding the self
 * participant. The self participant is the professional in the company inbox
 * context, or the current logged-in user in the personal inbox context.
 * @param conversation The conversation to get the participants from.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The participants of a chat conversation, excluding the self participant.
 */
export function getChatParticipantsExcludingSelf(
    conversation: Pick<Conversation, 'Participants'> | null | undefined,
    userID: number,
    professionalID?: number | null,
): Participant[] {
    if (!conversation) {
        return [];
    }
    let participants = conversation.Participants;
    if (professionalID && professionalID > 0) {
        return participants.filter(
            ({ Identity }) =>
                Identity.__typename !== 'Professional' ||
                Identity.ID !== professionalID,
        );
    }
    return participants.filter(
        ({ Identity }) =>
            Identity.__typename !== 'Member' || Identity.ID !== userID,
    );
}

/**
 * Get the identities of the participants of a chat conversation, excluding the
 * self participant. The self participant is the professional in the company
 * inbox context, or the current logged-in user in the personal inbox context.
 * @param conversation The conversation to get the participant identities from.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The identities of the participants of the chat conversation, excluding the self participant.
 */
export function getChatParticipantIdentitiesExcludingSelf(
    conversation: Pick<Conversation, 'Participants'> | null | undefined,
    userID: number,
    professionalID?: number | null,
) {
    const participants = getChatParticipantsExcludingSelf(
        conversation,
        userID,
        professionalID,
    );
    return participants.map(({ Identity }) => Identity);
}

/**
 * Get the participants of a chat to show in the avatar component.
 * @param conversation The conversation for the avatar.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The participants of the chat to show in the avatar component.
 */
export function getChatAvatarParticipants(
    conversation: Conversation | null | undefined,
    userID: number,
    professionalID?: number | null,
) {
    if (!conversation) {
        return [];
    }
    if (conversation.__typename === 'EnquiryConversation') {
        if (isEnquiryProfessionalSide(conversation, userID, professionalID)) {
            const enquirer = getEnquiryConversationEnquirer(conversation);
            if (enquirer) {
                return [enquirer];
            }
        }
        const professional = getEnquiryConversationProfessional(conversation);
        return [professional];
    }
    if (conversation.IsGroupChat) {
        // Include self participant in group chat avatars.
        return conversation.Participants.map(({ Identity }) => Identity);
    }
    return getChatParticipantIdentitiesExcludingSelf(
        conversation,
        userID,
        professionalID,
    );
}

/**
 * Get the user that sent the enquiry in an enquiry conversation. This assumes
 * that there is only one member non-collaborator participant in the conversation.
 * @param conversation The enquiry conversation to get the enquirer from.
 * @returns The user that sent the enquiry in the enquiry conversation.
 */
export function getEnquiryConversationEnquirer(
    conversation: EnquiryConversation,
) {
    const participant = conversation.Participants.find(
        ({ Identity, IsCollaborator }) =>
            Identity.__typename === 'Member' && !IsCollaborator,
    );
    if (participant?.Identity.__typename === 'Member') {
        return participant.Identity;
    }
    return null;
}

/**
 * Generate the title of a chat based on the inbox context, conversation type,
 * chat title and participants.
 * @param conversation The conversation to get the title for.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The title of the chat.
 */
export function getChatTitle(
    conversation: Conversation | null | undefined,
    userID: number,
    professionalID?: number | null,
): string {
    if (!conversation) {
        return '';
    }
    if (conversation.__typename === 'EnquiryConversation') {
        if (isEnquiryProfessionalSide(conversation, userID, professionalID)) {
            const enquirer = getEnquiryConversationEnquirer(conversation);
            if (enquirer) {
                return getParticipantName(enquirer);
            }
        }
        const professional = getEnquiryConversationProfessional(conversation);
        return professional.Title;
    }
    if (conversation.Name) {
        return conversation.Name;
    }
    const otherParticipants = getChatParticipantIdentitiesExcludingSelf(
        conversation,
        userID,
        professionalID,
    );
    if (conversation.IsGroupChat) {
        let groupChatTitle = otherParticipants
            .map((participant) => {
                return participant.__typename === 'Professional'
                    ? participant.Title
                    : participant.FirstName;
            })
            .join(', ');
        if (groupChatTitle) {
            groupChatTitle += ' and You';
        }
        return groupChatTitle;
    }
    const participant = otherParticipants[0];
    if (!participant) {
        return '';
    }
    return getParticipantName(participant);
}

/**
 * Generate the subtitle of a chat based on the inbox context, conversation
 * type, and participants.
 * @param conversation The conversation to get the subtitle for.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The subtitle of the chat.
 */
export function getChatSubtitle(
    conversation: Conversation | null | undefined,
    userID: number,
    professionalID?: number | null,
): string {
    if (!conversation) {
        return '';
    }
    if (conversation.__typename === 'EnquiryConversation') {
        if (isEnquiryProfessionalSide(conversation, userID, professionalID)) {
            const enquirer = getEnquiryConversationEnquirer(conversation);
            if (enquirer) {
                return getUserOccupation(enquirer);
            }
        }
        const professional = getEnquiryConversationProfessional(conversation);
        return professional.PrimaryCategory?.PrettyTitle ?? '';
    }
    if (conversation.BuildHub) {
        return conversation.BuildHub.Title;
    }
    if (conversation.IsGroupChat) {
        return '';
    }
    const otherParticipants = getChatParticipantIdentitiesExcludingSelf(
        conversation,
        userID,
        professionalID,
    );
    const otherParticipant = otherParticipants[0];
    if (!otherParticipant) {
        return '';
    }
    return otherParticipant.__typename === 'Professional'
        ? (otherParticipant.PrimaryCategory?.PrettyTitle ?? '')
        : getUserOccupation(otherParticipant);
}

/**
 * Get the names of the participants of a chat in the format: "John, Jane and Doe",
 * or "John, Jane and 2 others".
 * @param participants The participants of the chat.
 * @param maxSegments The maximum number of segments to include in the string.
 * @returns The names of the participants of the chat.
 */
export function getParticipantNames(
    participants: Participant[],
    maxSegments: number = 3,
): string {
    maxSegments = Math.max(maxSegments, 2);
    const otherCount =
        participants.length <= maxSegments
            ? 0
            : participants.length - maxSegments + 1;
    const names = participants
        .slice(0, otherCount ? maxSegments - 1 : maxSegments)
        .map(getParticipantName);
    if (otherCount > 0) {
        names.push(`${otherCount} others`);
    }
    if (names.length <= 1) {
        return names[0] ?? '';
    }
    return names.slice(0, -1).join(', ') + ' and ' + names.at(-1);
}

/**
 * Filter a list of conversations based on some filters. This is used to filter
 * conversations on the frontend, but we still need to filter on the backend as well.
 * @param conversations The list of conversations to filter.
 * @param userID The ID of the current user.
 * @param professionalID The ID of the professional if the conversations are scoped to a company inbox.
 * @param search The search query to filter the conversations by.
 * @param projectIDs The IDs of the BuildHub projects that the conversation must be linked to.
 * @returns The filtered list of conversations.
 * @see {@link app/src/Component/Conversation/Core/Service/ConversationService.php}
 */
export function filterChats({
    conversations,
    userID,
    professionalID,
    search = '',
    projectIDs = [],
}: {
    conversations: Conversation[];
    userID: number;
    professionalID?: number | null;
    search?: string;
    projectIDs?: string[];
}): Conversation[] {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    let filtered = [...conversations];
    // Must match the filter logic in ConversationService::getConversations.
    if (projectIDs.length > 0) {
        filtered = filtered.filter((conversation) => {
            if (
                conversation.__typename !== 'DirectConversation' ||
                !conversation.BuildHub
            ) {
                return false;
            }
            return projectIDs.includes(conversation.BuildHub.ID);
        });
    }
    if (normalizedSearch) {
        const searchTerms = normalizedSearch.split(/\s+/);
        filtered = filtered.filter((conversation) => {
            return searchTerms.every((term) => {
                if (conversation.__typename === 'DirectConversation') {
                    if (conversation.Name?.toLocaleLowerCase().includes(term)) {
                        return true;
                    }
                }
                const otherParticipants =
                    getChatParticipantIdentitiesExcludingSelf(
                        conversation,
                        userID,
                        professionalID,
                    );
                return otherParticipants.some((participant) => {
                    if (participant.__typename === 'Member') {
                        return (
                            participant.FirstName.toLocaleLowerCase().includes(
                                term,
                            ) ||
                            participant.LastName.toLocaleLowerCase().includes(
                                term,
                            )
                        );
                    }
                    return participant.Title.toLocaleLowerCase().includes(term);
                });
            });
        });
    }
    return filtered;
}

/**
 * Append the messages from the new conversation to the current conversation,
 * not including duplicate messages, and update the messages page info. This
 * is used to combine paginated messages from different pages. The other
 * properties are overriden by the new conversation.
 * @param current The current conversation to append the messages to.
 * @param newConversation The new conversation to append the messages from.
 * @returns The new conversation with merged messages.
 */
export function mergeChatMessages(
    current: DirectConversation | null,
    newConversation: DirectConversation | null,
): DirectConversation | null;
export function mergeChatMessages(
    current: EnquiryConversation | null,
    newConversation: EnquiryConversation | null,
): EnquiryConversation | null;
export function mergeChatMessages(
    current: DirectConversation | EnquiryConversation | null,
    newConversation: DirectConversation | EnquiryConversation | null,
): DirectConversation | EnquiryConversation | null {
    if (!current && !newConversation) {
        return null;
    }
    if (!current) {
        return newConversation;
    }
    if (!newConversation) {
        return current;
    }
    // Note: We have to write the logic twice to make the TypeScript types work
    // because Conversation can be either conversation type. The code in the if
    // else block is exactly the same as the first if block.
    if (
        current.__typename === 'DirectConversation' &&
        newConversation.__typename === 'DirectConversation'
    ) {
        const currentMessages = current.Messages;
        const newMessages = newConversation.Messages;
        let mergedPageInfo = newMessages.pageInfo ?? currentMessages.pageInfo;
        const mergedMessages = [...currentMessages.nodes];
        if (newMessages.nodes.length > 0) {
            if (mergedMessages.length > 0) {
                // Assume messages are always sorted by ID in descending order.
                const currentEndID = Number(currentMessages.nodes.at(-1)!.ID);
                const newStartID = Number(newMessages.nodes[0]!.ID);
                if (newStartID < currentEndID) {
                    // New conversation contains only older messages.
                    mergedMessages.push(...newMessages.nodes);
                    if (mergedPageInfo && newMessages.pageInfo) {
                        mergedPageInfo.hasNextPage =
                            newMessages.pageInfo.hasNextPage;
                    }
                } else {
                    // New conversation may contain newer, older, or existing messages.
                    const currentStartID = Number(currentMessages.nodes[0]!.ID);
                    const newEndID = Number(newMessages.nodes.at(-1)!.ID);
                    const newerMessages = newMessages.nodes.filter(
                        (message) => Number(message.ID) > currentStartID,
                    );
                    const olderMessages = newMessages.nodes.filter(
                        (message) => Number(message.ID) < currentEndID,
                    );
                    const existingMessages = newMessages.nodes.filter(
                        (message) =>
                            Number(message.ID) <= currentStartID &&
                            Number(message.ID) >= currentEndID,
                    );
                    // Update existing messages (may have been updated).
                    for (const upToDateMessage of existingMessages) {
                        const index = mergedMessages.findIndex(
                            (m) => m.ID === upToDateMessage.ID,
                        );
                        if (index !== -1) {
                            mergedMessages[index] = upToDateMessage;
                        }
                    }
                    mergedMessages.unshift(...newerMessages);
                    mergedMessages.push(...olderMessages);
                    if (mergedPageInfo) {
                        // Update the previous page.
                        if (
                            newStartID > currentStartID &&
                            newMessages.pageInfo
                        ) {
                            mergedPageInfo.hasPreviousPage =
                                newMessages.pageInfo.hasPreviousPage;
                        } else if (
                            currentStartID > newStartID &&
                            currentMessages.pageInfo
                        ) {
                            mergedPageInfo.hasPreviousPage =
                                currentMessages.pageInfo.hasPreviousPage;
                        }
                        // Update the next page.
                        if (newEndID < currentEndID && newMessages.pageInfo) {
                            mergedPageInfo.hasNextPage =
                                newMessages.pageInfo.hasNextPage;
                        } else if (
                            currentEndID < newEndID &&
                            currentMessages.pageInfo
                        ) {
                            mergedPageInfo.hasNextPage =
                                currentMessages.pageInfo.hasNextPage;
                        }
                    }
                }
            } else {
                mergedMessages.push(...newConversation.Messages.nodes);
                mergedPageInfo = newMessages.pageInfo;
            }
        } else {
            mergedPageInfo = currentMessages.pageInfo;
        }
        // Update the total count.
        if (mergedPageInfo) {
            mergedPageInfo.totalCount = Math.max(
                newMessages.pageInfo?.totalCount ?? 0,
                currentMessages.pageInfo?.totalCount ?? 0,
            );
        }

        return {
            ...newConversation,
            Messages: {
                ...newConversation.Messages,
                nodes: mergedMessages,
                pageInfo: mergedPageInfo,
            },
        };
    } else if (
        current.__typename === 'EnquiryConversation' &&
        newConversation.__typename === 'EnquiryConversation'
    ) {
        const currentMessages = current.Messages;
        const newMessages = newConversation.Messages;
        let mergedPageInfo = newMessages.pageInfo ?? currentMessages.pageInfo;
        const mergedMessages = [...currentMessages.nodes];
        if (newMessages.nodes.length > 0) {
            if (mergedMessages.length > 0) {
                // Assume messages are always sorted by ID in descending order.
                const currentEndID = Number(currentMessages.nodes.at(-1)!.ID);
                const newStartID = Number(newMessages.nodes[0]!.ID);
                if (newStartID < currentEndID) {
                    // New conversation contains only older messages.
                    mergedMessages.push(...newMessages.nodes);
                    if (mergedPageInfo && newMessages.pageInfo) {
                        mergedPageInfo.hasNextPage =
                            newMessages.pageInfo.hasNextPage;
                    }
                } else {
                    // New conversation may contain newer, older, or existing messages.
                    const currentStartID = Number(currentMessages.nodes[0]!.ID);
                    const newEndID = Number(newMessages.nodes.at(-1)!.ID);
                    const newerMessages = newMessages.nodes.filter(
                        (message) => Number(message.ID) > currentStartID,
                    );
                    const olderMessages = newMessages.nodes.filter(
                        (message) => Number(message.ID) < currentEndID,
                    );
                    const existingMessages = newMessages.nodes.filter(
                        (message) =>
                            Number(message.ID) <= currentStartID &&
                            Number(message.ID) >= currentEndID,
                    );
                    // Update existing messages (may have been updated).
                    for (const upToDateMessage of existingMessages) {
                        const index = mergedMessages.findIndex(
                            (m) => m.ID === upToDateMessage.ID,
                        );
                        if (index !== -1) {
                            mergedMessages[index] = upToDateMessage;
                        }
                    }
                    mergedMessages.unshift(...newerMessages);
                    mergedMessages.push(...olderMessages);
                    if (mergedPageInfo) {
                        // Update the previous page.
                        if (
                            newStartID > currentStartID &&
                            newMessages.pageInfo
                        ) {
                            mergedPageInfo.hasPreviousPage =
                                newMessages.pageInfo.hasPreviousPage;
                        } else if (
                            currentStartID > newStartID &&
                            currentMessages.pageInfo
                        ) {
                            mergedPageInfo.hasPreviousPage =
                                currentMessages.pageInfo.hasPreviousPage;
                        }
                        // Update the next page.
                        if (newEndID < currentEndID && newMessages.pageInfo) {
                            mergedPageInfo.hasNextPage =
                                newMessages.pageInfo.hasNextPage;
                        } else if (
                            currentEndID < newEndID &&
                            currentMessages.pageInfo
                        ) {
                            mergedPageInfo.hasNextPage =
                                currentMessages.pageInfo.hasNextPage;
                        }
                    }
                }
            } else {
                mergedMessages.push(...newConversation.Messages.nodes);
                mergedPageInfo = newMessages.pageInfo;
            }
        } else {
            mergedPageInfo = currentMessages.pageInfo;
        }
        // Update the total count.
        if (mergedPageInfo) {
            mergedPageInfo.totalCount = Math.max(
                newMessages.pageInfo?.totalCount ?? 0,
                currentMessages.pageInfo?.totalCount ?? 0,
            );
        }

        return {
            ...newConversation,
            Messages: {
                ...newConversation.Messages,
                nodes: mergedMessages,
                pageInfo: mergedPageInfo,
            },
        };
    }
    return newConversation;
}

/**
 * Convenience function to merge two conversations, appending the messages from
 * the new conversation to the current conversation. This function is required
 * to satisfy the TypeScript type system, otherwise the code will be more complex.
 * @param current The current conversation to append the messages to.
 * @param newConversation The new conversation to append the messages from.
 * @returns The merged conversation.
 */
export function mergeConversations(
    current: Conversation | null,
    newConversation: Conversation | null,
): Conversation | null {
    // Need to do this cursed logic to make TypeScript happy.
    if (!current || !newConversation) {
        return mergeChatMessages(
            // @ts-expect-error
            current,
            newConversation,
        );
    } else if (
        current.__typename === 'DirectConversation' &&
        newConversation.__typename === 'DirectConversation'
    ) {
        return mergeChatMessages(current, newConversation);
    } else if (
        current.__typename === 'EnquiryConversation' &&
        newConversation.__typename === 'EnquiryConversation'
    ) {
        return mergeChatMessages(current, newConversation);
    }
    return null;
}
